import React, { useState, useEffect } from "react";
import { NotificationContainer, NotificationManager } from "react-notifications";
import { useNavigate } from "react-router-dom";
import DashboardHeader from "../DashboardHeader/DashboardHeader";
import StepHeader from "../StepHeader/StepHeader";
import ActionBar from "../ActionBar/ActionBar";
import { APP_STEPS } from "../config/steps";
import "./FinanceInfo.css";
import WhatsAppWidget from "../WhatsAppWidget/WhatsAppWidget";

const FinanceDetails = () => {
  // ---- state (existing field names kept) ----
  const [financeDetails, setFinanceDetails] = useState({
    id: null,
    profession: "",
    income: "",
    dependents: "",
    scholarship: "No",
    organization: "",
    amount: "",
    payment_frequency: "",
    funds_request: "",
    funds_request_against: "",
    bank_name: "",
    account_title: "",
    account_number: "",
    iban: "",
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const applicationId = parseInt(localStorage.getItem("id"), 10);
  const status = localStorage.getItem("application_status");
  const isDisabled = status !== "incomplete";
  const currentStep = 2; // index in APP_STEPS

  // ---- fetch existing finance details ----
  useEffect(() => {
    const fetchFinanceDetails = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/finance-details?filters[application][id][$eq]=${applicationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) throw new Error("Failed to fetch finance details");
        const data = await response.json();

        if (data.data && data.data.length > 0) {
          const finance = data.data[0].attributes || data.data[0];
          setFinanceDetails({
            id: data.data[0].id,
            profession: finance.profession || "",
            income: finance.income || "",
            dependents: finance.dependents || "",
            funds_request: finance.funds_request || "",
            scholarship: finance.scholarship ? "Yes" : "No",
            amount_type: finance.amount_type || "",
            funds_request_against: finance.funds_request_against || "",
            organization: finance.organization || "",
            amount: finance.amount || "",
            payment_frequency: finance.payment_frequency || "",
            bank_name: finance.bank_name || "",
            account_title: finance.account_title || "",
            account_number: finance.account_number || "",
            iban: finance.iban || "",
            documentid: data.data[0].documentId || data.data[0].id,
          });
        }
      } catch (error) {
        console.error("Error fetching finance details:", error);
      }
    };
    fetchFinanceDetails();
  }, [applicationId, token]);

  // ---- input change ----
  const handleInputChange = (field, value) => {
    setFinanceDetails((prev) => ({ ...prev, [field]: value }));
  };

  // ---- stepper for dependents ----
  const handleDependents = (delta) => {
    const current = parseInt(financeDetails.dependents) || 0;
    const next = Math.max(0, current + delta);
    handleInputChange("dependents", String(next));
  };

  // ---- validation (existing rules kept) ----
  const validateForm = () => {
    const newErrors = {};
    if (!financeDetails.profession) newErrors.profession = "Profession is required.";
    if (!financeDetails.funds_request_against) newErrors.funds_request_against = "Funds Request Against is required.";
    if (!financeDetails.income) newErrors.income = "Income is required.";
    if (!financeDetails.dependents) newErrors.dependents = "Number of dependents is required.";
    if (!financeDetails.funds_request) newErrors.funds_request = "Funds Request is required.";
    if (!financeDetails.bank_name) newErrors.bank_name = "Bank name is required.";
    if (!financeDetails.account_title) newErrors.account_title = "Account title is required.";
    if (!financeDetails.account_number) newErrors.account_number = "Account number is required.";
    if (!financeDetails.iban) newErrors.iban = "IBAN is required.";

    if (financeDetails.scholarship === "Yes") {
      if (!financeDetails.organization) newErrors.organization = "Organization name is required.";
      if (!financeDetails.amount) newErrors.amount = "Amount is required.";
      if (!financeDetails.payment_frequency) newErrors.payment_frequency = "Payment frequency is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---- submit (existing API logic kept) ----
  const handleSubmit = async () => {
    if (isDisabled) return;
    if (status === "new") { navigate("/application/upload-documents"); return; }
    if (!validateForm()) return;
    setSaving(true);

    const financeData = {
      profession: financeDetails.profession,
      funds_request_against: financeDetails.funds_request_against,
      income: financeDetails.income,
      dependents: financeDetails.dependents,
      funds_request: financeDetails.funds_request,
      scholarship: financeDetails.scholarship === "Yes" ? true : financeDetails.scholarship === "No" ? false : null,
      organization: financeDetails.scholarship === "Yes" ? financeDetails.organization : "",
      amount: financeDetails.scholarship === "Yes" ? Number(financeDetails.amount) || null : null,
      payment_frequency: financeDetails.scholarship === "Yes" ? financeDetails.payment_frequency : "",
      bank_name: financeDetails.bank_name,
      account_title: financeDetails.account_title,
      account_number: financeDetails.account_number,
      iban: financeDetails.iban,
    };

    try {
      let response;
      if (financeDetails.id) {
        response = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/finance-details/${financeDetails.documentid}`,
          {
            method: "PUT",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ data: financeData }),
          }
        );
      } else {
        response = await fetch(
          `${process.env.REACT_APP_BASE_URL_strapi}/finance-details`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify({ data: { ...financeData, applicationId } }),
          }
        );
      }

      if (!response.ok) throw new Error("Failed to save finance details");

      const createdData = await response.json();
      NotificationManager.success("Finance details saved successfully!");
      localStorage.setItem("Finance_detail", createdData.id || createdData.data?.id);
      navigate("/application/upload-documents");
    } catch (error) {
      console.error("Error submitting finance details:", error);
      NotificationManager.error("Failed to save finance details. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // ---- helper: field error class ----
  const fieldClass = (field) => `fi__field${errors[field] ? " invalid" : ""}`;

  return (
    <div>
      {/* Top bar */}
      <DashboardHeader />

      {/* Step progress */}
      <div style={{ background: "rgba(255,255,255,.97)", borderBottom: "1px solid #dbe3e1" }}>
        <div className="fi" style={{ paddingBottom: 0 }}>
          <StepHeader steps={APP_STEPS} current={currentStep} />
        </div>
      </div>

      {/* Form */}
      <div className="fi">
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          {/* page heading */}
          <h2 className="fi__title">Financial details</h2>
          <p className="fi__sub">This helps us understand your family's situation. Please be accurate.</p>
          <p className="fi__req-note">Fields marked <em className="fi__req">*</em> are required.</p>

          {/* ---- Basic Information ---- */}
          <div className="fi__card">
            <div className={fieldClass("profession")}>
              <label className="fi__label">Profession of Father / Guardian <em className="fi__req">*</em></label>
              <input
                className="fi__input"
                placeholder="e.g. Shopkeeper, Teacher, Driver"
                value={financeDetails.profession}
                onChange={(e) => handleInputChange("profession", e.target.value)}
                disabled={isDisabled}
              />
              <p className="fi__err">{errors.profession}</p>
            </div>

            <div className={fieldClass("income")}>
              <label className="fi__label">Monthly Income of Father / Guardian <em className="fi__req">*</em></label>
              <div className="fi__prefix">
                <b>Rs</b>
                <input
                  type="number"
                  className="fi__input"
                  placeholder="0"
                  value={financeDetails.income}
                  onChange={(e) => handleInputChange("income", e.target.value)}
                  disabled={isDisabled}
                />
              </div>
              <p className="fi__hint">Enter total income for the last 12 months. Enter 0 if there is no income.</p>
              <p className="fi__err">{errors.income}</p>
            </div>

            <div className={fieldClass("dependents")}>
              <span className="fi__label">Number of Dependents on Father / Guardian</span>
              <div className="fi__stepper">
                <button type="button" onClick={() => handleDependents(-1)} disabled={isDisabled} aria-label="Decrease">−</button>
                <input
                  type="text"
                  className="fi__input"
                  value={financeDetails.dependents || "0"}
                  readOnly
                  disabled={isDisabled}
                />
                <button type="button" onClick={() => handleDependents(1)} disabled={isDisabled} aria-label="Increase">+</button>
              </div>
              <p className="fi__err">{errors.dependents}</p>
            </div>
          </div>

          {/* ---- Funding Request ---- */}
          <div className="fi__card">
            <h3>Funding request</h3>

            <div className={fieldClass("funds_request")}>
              <label className="fi__label">Requested Funding Amount <em className="fi__req">*</em></label>
              <div className="fi__prefix">
                <b>Rs</b>
                <input
                  type="number"
                  className="fi__input"
                  placeholder="0"
                  value={financeDetails.funds_request}
                  onChange={(e) => handleInputChange("funds_request", e.target.value)}
                  disabled={isDisabled}
                />
              </div>
              <p className="fi__err">{errors.funds_request}</p>
            </div>

            <div className={fieldClass("funds_request_against")}>
              <label className="fi__label">Requested Funding For <em className="fi__req">*</em></label>
              <select
                className="fi__select"
                value={financeDetails.funds_request_against || ""}
                onChange={(e) => handleInputChange("funds_request_against", e.target.value)}
                disabled={isDisabled}
              >
                <option value="">Please Select</option>
                <option value="College">College</option>
                <option value="Bachelors">Bachelors</option>
                <option value="Masters">Masters</option>
                <option value="Doctorate">Doctorate</option>
                <option value="Post Doctorate">Post Doctorate</option>
              </select>
              <p className="fi__err">{errors.funds_request_against}</p>
            </div>

            <div className={fieldClass("scholarship")}>
              <span className="fi__label">Are you getting any scholarship or stipend from another organization?</span>
              <div className="fi__seg">
                <label>
                  <input type="radio" name="scholarship" value="No"
                    checked={financeDetails.scholarship === "No"}
                    onChange={(e) => handleInputChange("scholarship", e.target.value)}
                    disabled={isDisabled}
                  />
                  <span>No</span>
                </label>
                <label>
                  <input type="radio" name="scholarship" value="Yes"
                    checked={financeDetails.scholarship === "Yes"}
                    onChange={(e) => handleInputChange("scholarship", e.target.value)}
                    disabled={isDisabled}
                  />
                  <span>Yes</span>
                </label>
              </div>
            </div>

            {/* Conditional: scholarship details */}
            {financeDetails.scholarship === "Yes" && (
              <div className="fi__cond">
                <div className={fieldClass("organization")}>
                  <label className="fi__label">Name of Organization <em className="fi__req">*</em></label>
                  <input
                    className="fi__input"
                    placeholder="Who gives you the scholarship?"
                    value={financeDetails.organization}
                    onChange={(e) => handleInputChange("organization", e.target.value)}
                    disabled={isDisabled}
                  />
                  <p className="fi__err">{errors.organization}</p>
                </div>

                <div className={fieldClass("amount")}>
                  <label className="fi__label">Receiving Amount <em className="fi__req">*</em></label>
                  <div className="fi__prefix">
                    <b>Rs</b>
                    <input
                      type="number"
                      className="fi__input"
                      placeholder="0"
                      value={financeDetails.amount}
                      onChange={(e) => handleInputChange("amount", e.target.value)}
                      disabled={isDisabled}
                    />
                  </div>
                  <p className="fi__err">{errors.amount}</p>
                </div>

                <div className={fieldClass("payment_frequency")}>
                  <span className="fi__label">Amount received is</span>
                  <div className="fi__seg">
                    <label>
                      <input type="radio" name="payment_frequency" value="Monthly"
                        checked={financeDetails.payment_frequency === "Monthly"}
                        onChange={(e) => handleInputChange("payment_frequency", e.target.value)}
                        disabled={isDisabled}
                      />
                      <span>Monthly</span>
                    </label>
                    <label>
                      <input type="radio" name="payment_frequency" value="Annually"
                        checked={financeDetails.payment_frequency === "Annually"}
                        onChange={(e) => handleInputChange("payment_frequency", e.target.value)}
                        disabled={isDisabled}
                      />
                      <span>Annually</span>
                    </label>
                    <label>
                      <input type="radio" name="payment_frequency" value="Completion on Degree"
                        checked={financeDetails.payment_frequency === "Completion on Degree"}
                        onChange={(e) => handleInputChange("payment_frequency", e.target.value)}
                        disabled={isDisabled}
                      />
                      <span>On Degree</span>
                    </label>
                  </div>
                  <p className="fi__err">{errors.payment_frequency}</p>
                </div>
              </div>
            )}
          </div>

          {/* ---- Bank Information ---- */}
          <div className="fi__card">
            <h3>Bank information</h3>
            <p className="fi__hint" style={{ marginTop: "-8px", marginBottom: "14px" }}>Where should we transfer your scholarship funds?</p>

            <div className="fi__note">
              <span>&#9888;&#65039;</span>
              <span>
                Bank information must belong to the <strong>applicant&rsquo;s father or guardian</strong> only.
                Any other person&rsquo;s bank details will <strong>not be accepted</strong>, and the application will be
                <strong> considered as rejected</strong>.
              </span>
            </div>

            <div className={fieldClass("bank_name")}>
              <label className="fi__label">Bank Name <em className="fi__req">*</em></label>
              <input
                className="fi__input"
                placeholder="e.g. HBL, UBL, MCB"
                value={financeDetails.bank_name}
                onChange={(e) => handleInputChange("bank_name", e.target.value)}
                disabled={isDisabled}
              />
              <p className="fi__err">{errors.bank_name}</p>
            </div>

            <div className="fi__row2">
              <div className={fieldClass("account_title")}>
                <label className="fi__label">Account Title <em className="fi__req">*</em></label>
                <input
                  className="fi__input"
                  placeholder="Name on account"
                  value={financeDetails.account_title}
                  onChange={(e) => handleInputChange("account_title", e.target.value)}
                  disabled={isDisabled}
                />
                <p className="fi__err">{errors.account_title}</p>
              </div>
              <div className={fieldClass("account_number")}>
                <label className="fi__label">Account Number <em className="fi__req">*</em></label>
                <input
                  className="fi__input"
                  placeholder="e.g. 01234567890123"
                  value={financeDetails.account_number}
                  onChange={(e) => handleInputChange("account_number", e.target.value)}
                  disabled={isDisabled}
                />
                <p className="fi__err">{errors.account_number}</p>
              </div>
            </div>

            <div className={fieldClass("iban")}>
              <label className="fi__label">IBAN <em className="fi__req">*</em></label>
              <input
                className="fi__input"
                placeholder="e.g. PK36SCBL0000001123456702"
                value={financeDetails.iban}
                onChange={(e) => handleInputChange("iban", e.target.value)}
                disabled={isDisabled}
                maxLength={29}
              />
              <p className="fi__hint">29-character IBAN starting with PK</p>
              <p className="fi__err">{errors.iban}</p>
            </div>
          </div>
        </form>
      </div>

      {/* Bottom action bar */}
      <ActionBar
        onBack={() => navigate("/application/academic-information")}
        onNext={handleSubmit}
        backLabel="← Back"
        nextLabel="Continue →"
        nextDisabled={isDisabled}
        nextLoading={saving}
      />

      <NotificationContainer />
      <WhatsAppWidget />
    </div>
  );
};

export default FinanceDetails;
